import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KPICard, { KPIGrid } from '../components/KPICard';
import { useAssistantPageExtras } from '../components/assistant/AssistantContext';
import { getDeal } from '../deals/registry.js';
import {
  absAroIsUnknown,
  absAroTreatmentLabel,
  absDeckProvenanceLabel,
  absIsOil,
  absCoveragePriceRows,
  absOutputMatchesDeal,
  absPriceSeriesLabel,
  absRowPrice,
  absSensitivityRows,
  buildAbsProvenance,
  buildHedgePanel,
  buildScreenColumns,
  downsideColumnHeader,
  formatPackageSourceLabel,
  formatPresetProvenance,
  resolveAroTreatment,
  selectAbsOutputForDeal,
} from './absDisplay.js';
import { absApi } from './api';
import AbsLink from './AbsLink.jsx';
import Financials from './Financials.jsx';

const SELF_TABS = new Set(['link', 'financials']);

export const ABS_TABS = [
  { key: 'financials', label: 'Financials' },
  { key: 'summary', label: 'Summary' },
  { key: 'structure', label: 'Structure' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'bid', label: 'Bid' },
  { key: 'sensitivity', label: 'Sensitivity' },
  { key: 'scenarios', label: 'Desk presets' },
  { key: 'link', label: 'ABS link' },
];

function fmtMM(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}MM`;
}

function fmtPct(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

function fmtX(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `${Number(n).toFixed(2)}x`;
}

const DEFAULT_STRUCTURE = {
  advPv10: 0.60,
  advPp: 0.75,
  targetDscr: 1.25,
  coupon: 0.085,
  tenorYears: 7,
  debtFeePct: 0.01,
  aroReserveMM: 0,
  aroTreatedAsZero: false,
  aroUnknown: false,
};

/**
 * Last screen per deal (module scope survives tab switches / leaving ABS). Revisits paint
 * this instantly — no spinner, no engine run. Only "Re-run ABS" (force) or an input change
 * recomputes; the server also answers identical inputs from its cache.
 */
const screenCache = new Map();
/** Bumped on logout clear so an in-flight run cannot refill the cache for the next session. */
let screenCacheEpoch = 0;

/** Plain-words labels for counterparty-export blockers (raw codes stay in the tooltip). */
const ELIGIBILITY_REASON_LABEL = {
  v0_package: 'v0 package (not engineer-certified)',
  case_primary_sponsor_model: 'sponsor model is the primary case',
  ad_valorem_missing: 'ad valorem tax missing',
  downside_not_run: 'downside not run',
  preset_stale: 'desk preset stale',
  proposed_price_missing: 'no proposed purchase price',
  fields_not_modeled: 'some fields not modeled',
  deducts_not_visible: 'deducts not visible',
  run_not_durable: 'run not saved',
};
export function _clearAbsScreenCache() {
  screenCacheEpoch += 1;
  screenCache.clear();
}

export default function AbsDesk({ tab = 'summary', onNavigateTab, dealId = 'eagle-etx-2026' }) {
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const dealMeta = useMemo(() => getDeal(dealId), [dealId]);
  const [structure, setStructure] = useState(DEFAULT_STRUCTURE);
  const [output, setOutput] = useState(null);
  const [screen, setScreen] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [hedgeView, setHedgeView] = useState('agency');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  /** Bumps on every deal switch / new run so late responses cannot paint the wrong desk. */
  const runGenRef = useRef(0);

  const applyStructureFromResult = useCallback((result) => {
    if (!result?.structure) return;
    setStructure((prev) => ({
      ...prev,
      advPv10: result.structure.advPv10 ?? prev.advPv10,
      advPp: result.structure.advPp ?? prev.advPp,
      targetDscr: result.structure.targetDscr ?? prev.targetDscr,
      coupon: result.structure.coupon ?? prev.coupon,
      tenorYears: result.structure.tenorYears ?? prev.tenorYears,
      debtFeePct: result.structure.debtFeePct ?? prev.debtFeePct,
      aroReserveMM: result.structure.aroReserveMM ?? prev.aroReserveMM,
      aroTreatedAsZero:
        result.solve?.aroTreatment === 'treatedAsZero' ||
        Boolean(result.structure.aroTreatedAsZero),
      aroUnknown:
        result.solve?.aroTreatment === 'unknown' || Boolean(result.structure.aroUnknown),
    }));
  }, []);

  const run = useCallback(async (struct = structure, { force = false } = {}) => {
    const requested = dealId;
    const gen = ++runGenRef.current;
    const epoch = screenCacheEpoch;
    setRunning(true);
    setError(null);
    try {
      const result = await absApi.screen({
        dealId: requested,
        structure: struct,
        proposedPurchasePriceMM: struct.proposedPurchasePriceMM ?? null,
        generateProgramHedges: true,
        ...(force ? { force: true } : {}),
      });
      if (gen !== runGenRef.current) return;
      if (epoch !== screenCacheEpoch) return;
      if (absOutputMatchesDeal(result?.base || result, requested)) {
        screenCache.set(requested, { structure: struct, result });
      }
      // /screen returns { base, downside, ... }; tolerate a bare run payload.
      const base = result?.base || result;
      if (!absOutputMatchesDeal(base, requested)) {
        setOutput(null);
        setScreen(null);
        setError(`ABS package does not match selected deal (${requested})`);
        return;
      }
      setScreen(result?.base ? result : null);
      setOutput(base);
      applyStructureFromResult(base);
      const elig = result.exportEligibility || { eligible: false, reasons: ['downside_not_run'] };
      setEligibility({
        eligible: Boolean(elig.eligible),
        reasons: elig.reasons || elig.counterpartyPack?.reasons || [],
      });
    } catch (e) {
      if (gen !== runGenRef.current) return;
      setError(e.message || String(e));
    } finally {
      if (gen === runGenRef.current) {
        setLoading(false);
        setRunning(false);
      }
    }
  }, [structure, dealId, applyStructureFromResult]);

  // Deal switch: clear prior desk immediately — never leave Eagle chrome under Juno/Sedgwick.
  useEffect(() => {
    runGenRef.current += 1;
    const gen = runGenRef.current;
    const requested = dealId;
    const cached = screenCache.get(requested);
    const reqStructure = cached?.structure || DEFAULT_STRUCTURE;
    const paint = (result) => {
      const base = result?.base || result;
      setScreen(result?.base ? result : null);
      setOutput(base);
      applyStructureFromResult(base);
      const elig = result.exportEligibility || { eligible: false, reasons: ['downside_not_run'] };
      setEligibility({
        eligible: Boolean(elig.eligible),
        reasons: elig.reasons || elig.counterpartyPack?.reasons || [],
      });
    };
    setError(null);
    setStructure(reqStructure);
    if (cached) {
      // Revisit: paint last result instantly, then quietly revalidate (server cache hit
      // unless the package changed, e.g. new ARO stamps).
      paint(cached.result);
      setLoading(false);
      setRunning(false);
    } else {
      setOutput(null);
      setScreen(null);
      setEligibility(null);
      setLoading(true);
      setRunning(true);
    }
    const quiet = Boolean(cached);
    const epoch = screenCacheEpoch;

    let cancelled = false;
    (async () => {
      try {
        const result = await absApi.screen({
          dealId: requested,
          structure: reqStructure,
          proposedPurchasePriceMM: reqStructure.proposedPurchasePriceMM ?? null,
          generateProgramHedges: true,
        });
        if (cancelled || gen !== runGenRef.current) return;
        // /screen returns { base, downside, ... }; tolerate a bare run payload.
        const base = result?.base || result;
        if (quiet && result?.screenId && result.screenId === cached.result?.screenId) return;
        if (!absOutputMatchesDeal(base, requested)) {
          if (quiet) return;
          setOutput(null);
          setScreen(null);
          setError(`ABS package does not match selected deal (${requested})`);
          return;
        }
        if (epoch !== screenCacheEpoch) return;
        screenCache.set(requested, { structure: reqStructure, result });
        paint(result);
      } catch (e) {
        if (cancelled || gen !== runGenRef.current) return;
        if (!quiet) setError(e.message || String(e));
      } finally {
        if (!cancelled && gen === runGenRef.current && !quiet) {
          setLoading(false);
          setRunning(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dealId, applyStructureFromResult]);

  // Only paint results that belong to the current selection (stale Eagle discarded).
  const displayOutput = selectAbsOutputForDeal(output, dealId);
  const solve = displayOutput?.solve;
  const summary = displayOutput?.summary;
  const coverage = displayOutput?.coverage;
  const isDemo = Boolean(displayOutput?.meta?.demo);
  const oil = displayOutput
    ? absIsOil(displayOutput)
    : Boolean(dealMeta && dealMeta.primaryProduct === 'oil');
  const priceLabel = displayOutput
    ? absPriceSeriesLabel(displayOutput)
    : absPriceSeriesLabel(dealMeta?.primaryProduct || null);
  const provenance = useMemo(
    () => (displayOutput ? buildAbsProvenance(displayOutput, dealId) : null),
    [displayOutput, dealId],
  );
  const aroTreatment = useMemo(
    () => (displayOutput ? resolveAroTreatment(displayOutput) : null),
    [displayOutput],
  );
  const aroUnknown = displayOutput ? absAroIsUnknown(displayOutput) : false;
  const underwriting = displayOutput?.underwritingStatus || solve?.underwritingStatus || null;

  const headline = useMemo(() => summary?.headline || solve || {}, [summary, solve]);
  const screenColumns = useMemo(
    () => (screen ? buildScreenColumns(screen) : null),
    [screen],
  );
  const hedgePanel = useMemo(
    () => (displayOutput ? buildHedgePanel(displayOutput, hedgeView) : null),
    [displayOutput, hedgeView],
  );

  // Chrome always keyed to the *requested* deal while loading — never prior package title.
  const titlePackage =
    summary?.package || dealMeta?.packageName || dealMeta?.label || dealId || 'ABS package';
  const titleDealId = provenance?.requestedDealId || dealId;

  // Same personal-agent AssistantPanel as VDR — publish live ABS knobs/results.
  useEffect(() => {
    setPageExtras({
      surface: 'abs',
      absTab: tab,
      structure,
      headline: displayOutput
        ? {
            illustrativeMaxPurchasePriceMM: headline.illustrativeMaxPurchasePriceMM ?? headline.maxBidMM,
            bindingConstraint: headline.bindingConstraint,
            maxNoteSizeMM: headline.maxNoteSizeMM,
            minDscr: headline.minDscr,
          }
        : null,
      dealId: dealId || null,
      packageName: summary?.package || dealMeta?.packageName || null,
      demo: displayOutput ? isDemo : null,
      packageSource: provenance?.packageSource || null,
      absLoading: loading || !displayOutput,
    });
    return () => clearPageExtras();
  }, [
    tab,
    structure,
    headline,
    summary,
    isDemo,
    provenance,
    dealId,
    dealMeta,
    displayOutput,
    loading,
    setPageExtras,
    clearPageExtras,
  ]);

  const onStructChange = (key, raw) => {
    if (key === 'aroTreatedAsZero' || key === 'aroUnknown') {
      const on = Boolean(raw);
      setStructure((prev) => ({
        ...prev,
        [key]: on,
        // Mutual exclusion on the desk — structure flags must not both be true.
        ...(key === 'aroTreatedAsZero' && on ? { aroUnknown: false } : {}),
        ...(key === 'aroUnknown' && on ? { aroTreatedAsZero: false } : {}),
      }));
      return;
    }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    setStructure((prev) => ({ ...prev, [key]: n }));
  };

  return (
    <div className="abs-desk" data-abs-deal={dealId} data-testid="abs-desk">
      <div className="abs-desk-header">
        <div>
          <p className="abs-kicker">ABS mode · note-buyer desk</p>
          <h2 className="abs-title">
            {titlePackage}{' '}
            <span className="abs-deal-id" data-testid="abs-title-deal-id">
              {titleDealId}
            </span>
          </h2>
          <p className="abs-lead">
            Capital tape from DealPackage → coverage / advance bases / illustrative max purchase price.
            Sponsor assumptions — not financing commitment.
          </p>
        </div>
        <button
          type="button"
          className="abs-run-btn"
          disabled={running}
          onClick={() => run(structure, { force: true })}
        >
          {running ? 'Running…' : 'Re-run ABS'}
        </button>
      </div>

      {provenance ? (
        <AbsProvenanceStrip provenance={provenance} />
      ) : (
        <div
          className="abs-provenance abs-provenance-loading"
          data-testid="abs-provenance-loading"
          role="status"
          aria-label="ABS package loading"
        >
          <div className="abs-provenance-item">
            <span className="abs-provenance-key">Deal</span>
            <code className="abs-provenance-val" data-testid="abs-provenance-deal">
              {dealId}
            </code>
          </div>
          <div className="abs-provenance-item">
            <span className="abs-provenance-key">Mode</span>
            <span className="abs-provenance-mode">Loading…</span>
          </div>
        </div>
      )}

      {/* Link/Financials run their own pipeline and show their own ARO choice. */}
      {!SELF_TABS.has(tab) && (
      <div className="abs-banner-row">
        <div className="abs-banner abs-banner-warn">
          Desk presets are classified MODEL / FACT / JUDGMENT with asOf — not market quotes.
          Agency view credits executed hedges only.
        </div>
        {isDemo && displayOutput && (
          <div className="abs-banner abs-banner-demo">
            DEMO package economics (smoke fixture) — wire live <code>deal-package.json</code> for CA numbers.
          </div>
        )}
        {displayOutput && aroTreatment && (
          <div
            className={`abs-banner ${aroUnknown ? 'abs-banner-block' : 'abs-banner-aro'}`}
            data-testid="abs-aro-banner"
            data-aro-treatment={aroTreatment}
            role="status"
          >
            <strong>ARO:</strong> {absAroTreatmentLabel(aroTreatment)}
            {underwriting?.status === 'blocked' && (
              <>
                {' '}
                · underwriting blocked
                {Array.isArray(underwriting.blockers) && underwriting.blockers.length > 0
                  ? ` (${underwriting.blockers.join(', ')})`
                  : ''}
              </>
            )}
          </div>
        )}
      </div>
      )}

      <nav className="abs-subnav" aria-label="ABS sections">
        {ABS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`abs-subnav-btn ${tab === t.key ? 'active' : ''}`}
            onClick={() => onNavigateTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'link' && <AbsLink key={dealId} dealId={dealId} />}
      {tab === 'financials' && <Financials key={dealId} dealId={dealId} />}

      {!SELF_TABS.has(tab) && (loading || !displayOutput) && !error && (
        <div className="loading-screen" data-testid="abs-loading">
          <div className="loading-spinner" />
          <p>Running ABS engine for <code>{dealId}</code>…</p>
        </div>
      )}

      {!SELF_TABS.has(tab) && error && (
        <div className="error-screen">
          <h2>ABS run failed</h2>
          <p>{error}</p>
          <button type="button" className="abs-run-btn" onClick={() => run(structure)}>
            Retry
          </button>
        </div>
      )}

      {!SELF_TABS.has(tab) && !loading && !error && displayOutput && (
        <>
          <KPIGrid>
            <KPICard
              label="Illustrative max purchase price (sponsor assumptions)"
              value={fmtMM(headline.illustrativeMaxPurchasePriceMM ?? solve?.illustrativeMaxPurchasePriceMM)}
              sub="executable note × (1 − fee) ÷ advPp"
              color="var(--amber)"
            />
            <KPICard
              label="Binding constraint"
              value={solve?.bindingConstraint || '—'}
              sub="which limit sets note size"
              color="var(--orange)"
            />
            <KPICard
              label="Max note size"
              value={fmtMM(solve?.maxNoteSizeMM)}
              sub={`advPv10 ${fmtPct(solve?.advPv10 ?? structure.advPv10)} · advPp ${fmtPct(solve?.advPp ?? structure.advPp)}`}
              color="var(--blue)"
            />
            <KPICard
              label="Min DSCR"
              value={fmtX(coverage?.minDscr)}
              sub={`target ${fmtX(structure.targetDscr)} · ${coverage?.bindingMonth || ''}`}
              color="var(--teal)"
            />
          </KPIGrid>

          {screenColumns && (
            <section className="abs-panel abs-screen-pair" data-testid="abs-screen-pair">
              <h3>BASE vs downside</h3>
              <p className="abs-panel-lead">
                Always shown side by side. Numbers only — status enums are not rendered.
              </p>
              <div className="abs-screen-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {['base', 'downside'].map((key) => {
                  const col = screenColumns[key];
                  const couponParam = (col.params || []).find((p) => p.key === 'coupon');
                  return (
                    <div key={key} data-testid={`abs-screen-col-${key}`}>
                      <h4>{col.title}</h4>
                      {col.failed && key === 'downside' ? (
                        <p className="abs-panel-lead">Downside run unavailable — BASE still shown.</p>
                      ) : null}
                      <dl className="abs-dl">
                        <div>
                          <dt>Illustrative max purchase price</dt>
                          <dd className="abs-em">
                            {fmtMM(col.bidMM)}
                            {couponParam ? (
                              <span className="abs-provenance-val">
                                {' '}
                                {formatPresetProvenance(couponParam)}
                              </span>
                            ) : null}
                          </dd>
                        </div>
                        <div>
                          <dt>Max note</dt>
                          <dd>{fmtMM(col.noteMM)}</dd>
                        </div>
                        <div>
                          <dt>Min DSCR</dt>
                          <dd>{fmtX(col.minDscr)}</dd>
                        </div>
                      </dl>
                    </div>
                  );
                })}
              </div>
              <div className="abs-export-row">
                <button
                  type="button"
                  className="abs-run-btn"
                  disabled={!eligibility?.eligible}
                  title={
                    eligibility?.eligible
                      ? 'Eligible for counterparty pack'
                      : (eligibility?.reasons || []).join(', ') || 'Not eligible'
                  }
                  data-testid="abs-export-counterparty"
                >
                  Export for counterparty
                </button>
                {!eligibility?.eligible && (eligibility?.reasons || []).length > 0 && (
                  <div className="abs-elig-reasons" data-testid="abs-eligibility-reasons">
                    <span className="abs-elig-label">Not export-ready:</span>
                    {(eligibility?.reasons || []).map((r) => (
                      <span key={r} className="abs-elig-chip" title={r}>
                        {ELIGIBILITY_REASON_LABEL[r] || r.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {hedgePanel && (
            <section className="abs-panel" data-testid="abs-hedge-panel">
              <h3>Hedge program</h3>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  className={`abs-subnav-btn ${hedgeView === 'agency' ? 'active' : ''}`}
                  onClick={() => setHedgeView('agency')}
                >
                  Agency view
                </button>
                <button
                  type="button"
                  className={`abs-subnav-btn ${hedgeView === 'sponsor' ? 'active' : ''}`}
                  onClick={() => setHedgeView('sponsor')}
                >
                  Sponsor view
                </button>
              </div>
              <dl className="abs-dl">
                <div>
                  <dt>Floor / cap</dt>
                  <dd>
                    {hedgePanel.program
                      ? `${fmtPct(hedgePanel.program.floorPct)} / ${fmtPct(hedgePanel.program.capPct)}`
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Executed / proposed</dt>
                  <dd>
                    {hedgePanel.counts.executed} / {hedgePanel.counts.proposed}
                  </dd>
                </div>
                <div>
                  <dt>Over-hedge months</dt>
                  <dd>{hedgePanel.overhedgedMonths}</dd>
                </div>
              </dl>
            </section>
          )}

          {tab === 'summary' && (
            <AbsSummary output={displayOutput} />
          )}
          {tab === 'structure' && (
            <AbsStructure
              structure={structure}
              onChange={onStructChange}
              onRun={() => run(structure)}
              running={running}
              labels={displayOutput.structure?.labels}
            />
          )}
          {tab === 'coverage' && (
            <AbsCoverage coverage={coverage} output={displayOutput} />
          )}
          {tab === 'bid' && <AbsBid output={displayOutput} />}
          {tab === 'sensitivity' && (
            <AbsSensitivity
              model={absSensitivityRows(displayOutput, screen)}
              oil={oil}
              priceLabel={priceLabel}
            />
          )}
          {tab === 'scenarios' && (
            <AbsDeskPresets
              screen={screen}
              downsideTitle={screen ? downsideColumnHeader(screen) : null}
            />
          )}
        </>
      )}
    </div>
  );
}

function AbsProvenanceStrip({ provenance }) {
  const {
    requestedDealId,
    packageSource,
    demo,
    live,
    modeLabel,
    runHash,
    ingestVersion,
  } = provenance;
  return (
    <div
      className={`abs-provenance ${demo ? 'abs-provenance-demo' : live ? 'abs-provenance-live' : ''}`}
      data-testid="abs-provenance"
      role="status"
      aria-label="ABS package provenance"
    >
      <div className="abs-provenance-item">
        <span className="abs-provenance-key">Deal</span>
        <code className="abs-provenance-val" data-testid="abs-provenance-deal">
          {requestedDealId || '—'}
        </code>
      </div>
      <div className="abs-provenance-item">
        <span className="abs-provenance-key">Source</span>
        <code className="abs-provenance-val" data-testid="abs-provenance-source">
          {formatPackageSourceLabel(packageSource)}
        </code>
      </div>
      <div className="abs-provenance-item">
        <span className="abs-provenance-key">Mode</span>
        <span
          className={`abs-provenance-mode ${demo ? 'is-demo' : live ? 'is-live' : ''}`}
          data-testid="abs-provenance-mode"
        >
          {modeLabel}
        </span>
      </div>
      {ingestVersion && (
        <div className="abs-provenance-item">
          <span className="abs-provenance-key">Ingest</span>
          <code className="abs-provenance-val" data-testid="abs-provenance-ingest">
            {ingestVersion}
          </code>
        </div>
      )}
      {runHash && (
        <div className="abs-provenance-item">
          <span className="abs-provenance-key">Run</span>
          <code className="abs-provenance-val" data-testid="abs-provenance-runhash" title={runHash}>
            {runHash}
          </code>
        </div>
      )}
    </div>
  );
}

function AbsSummary({ output }) {
  const s = output.summary || {};
  const nonClaims = s.nonClaims || [];
  const preset = output.meta?.preset || null;
  const aroTreatment = resolveAroTreatment(output);
  const uw = output.underwritingStatus || output.solve?.underwritingStatus;
  const primary = output.cases?.primary || {
    caseSource: output.meta?.caseSource,
    firm: output.summary?.cases?.primary?.firm,
    effectiveDate: output.cases?.primary?.effectiveDate,
  };
  const caseSource = primary?.caseSource || output.meta?.caseSource || '—';
  const firmLabel = primary?.firm || (caseSource === 'sponsorModel' ? 'no firm' : 'no firm');
  const eff = primary?.effectiveDate || '—';
  const challengers = output.cases?.challengers || [];
  const excluded = output.collateral?.excluded || [];
  return (
    <section className="abs-panel">
      <h3>Note-buyer summary</h3>
      <p className="abs-panel-lead" data-testid="abs-primary-case-line">
        Primary case: {caseSource} — {firmLabel} — effective {eff}
      </p>
      <p className="abs-panel-lead">
        Headline capital tape for independent-sponsor / credit review. Structure knobs are labeled assumptions.
      </p>
      <dl className="abs-dl">
        <div><dt>Deal</dt><dd>{s.dealId}</dd></div>
        <div><dt>Seller</dt><dd>{s.seller || '—'}</dd></div>
        <div><dt>Ingest</dt><dd>{s.ingestVersion || 'v0'}</dd></div>
        <div><dt>Collateral PV</dt><dd>{fmtMM(s.headline?.collateralPvMM)}</dd></div>
        <div><dt>ARO treatment</dt><dd data-testid="abs-aro-treatment">{aroTreatment || '—'}</dd></div>
        <div>
          <dt>Underwriting</dt>
          <dd data-testid="abs-underwriting-status">
            {uw?.status || '—'}
            {Array.isArray(uw?.blockers) && uw.blockers.length
              ? ` · ${uw.blockers.join(', ')}`
              : ''}
          </dd>
        </div>
        <div><dt>Year-1 CF</dt><dd>{fmtMM(s.headline?.year1CfMM)}</dd></div>
        <div><dt>Run hash</dt><dd><code>{output.meta?.runHash}</code></dd></div>
      </dl>
      {challengers.length > 0 && (
        <>
          <h4>Challenger deltas</h4>
          <ul className="abs-list" data-testid="abs-challenger-deltas">
            {challengers.map((c) => (
              <li key={c.caseId}>
                <strong>{c.caseId}</strong>
                {' — '}
                {c.caseSource}
                {' · PV '}
                {fmtMM(c.pvMM)}
                {' · Δ '}
                {fmtMM(c.deltaVsPrimaryMM)}
                {c.label ? ` · ${c.label}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {excluded.length > 0 && (
        <>
          <h4>Excluded wells (non-PDP revenue and costs)</h4>
          <ul className="abs-list" data-testid="abs-excluded-wells">
            {excluded.map((e) => (
              <li key={e.wellId}>
                {e.wellId} ({e.resCat}) — excluded revenue PV {fmtMM(e.excludedRevenuePvMM)}
                ; retained cost PV {fmtMM(e.retainedCostPvMM)}
              </li>
            ))}
          </ul>
        </>
      )}
      {preset && (
        <>
          <h4>Desk preset</h4>
          <ul className="abs-list" data-testid="abs-desk-preset-summary">
            <li>
              <strong>{preset.presetId === 'first-issuer-downside' ? (preset.subtitle || preset.label) : preset.label}</strong>
              {preset.stale ? ' · stale' : ''}
            </li>
            {(preset.params || []).slice(0, 6).map((p) => (
              <li key={p.paramId}>
                {p.key}: {String(p.value)}{' '}
                <span className="abs-provenance-val">{formatPresetProvenance(p)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <h4>Non-claims</h4>
      <ul className="abs-list">
        {nonClaims.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
    </section>
  );
}

function AbsStructure({ structure, onChange, onRun, running, labels }) {
  const fields = [
    {
      key: 'advPv10',
      label: 'Advance vs PV-10 (advPv10)',
      step: 0.01,
      min: 0.05,
      max: 0.95,
      fmt: 'pct',
    },
    {
      key: 'advPp',
      label: 'Advance vs purchase price (advPp)',
      step: 0.01,
      min: 0.05,
      max: 0.95,
      fmt: 'pct',
    },
    { key: 'targetDscr', label: 'Target DSCR', step: 0.05, min: 1.0, max: 3.0, fmt: 'x' },
    { key: 'coupon', label: 'Coupon (illustrative)', step: 0.005, min: 0.01, max: 0.25, fmt: 'pct' },
    { key: 'tenorYears', label: 'Tenor (years)', step: 1, min: 1, max: 20, fmt: 'num' },
    { key: 'debtFeePct', label: 'Debt fee (up front)', step: 0.005, min: 0, max: 0.1, fmt: 'pct' },
    { key: 'aroReserveMM', label: 'ARO reserve ($MM)', step: 0.25, min: 0, max: 50, fmt: 'mm' },
  ];
  return (
    <section className="abs-panel">
      <h3>Structure knobs</h3>
      <p className="abs-panel-lead">
        All values are sponsor assumptions until a named counterparty confirms. Coupon is a placeholder.
        Zero ARO requires an explicit treatment — silent $0 does not inflate collateral.
      </p>
      <div className="abs-knob-grid">
        {fields.map((f) => (
          <label key={f.key} className="abs-knob">
            <span className="abs-knob-label">{f.label}</span>
            <input
              type="number"
              step={f.step}
              min={f.min}
              max={f.max}
              value={structure[f.key]}
              onChange={(e) => onChange(f.key, e.target.value)}
            />
            {labels?.[f.key] && <span className="abs-knob-hint">{labels[f.key]}</span>}
          </label>
        ))}
      </div>
      <div className="abs-aro-flags" data-testid="abs-aro-flags">
        <label className="abs-knob abs-knob-check">
          <input
            type="checkbox"
            checked={Boolean(structure.aroTreatedAsZero)}
            onChange={(e) => onChange('aroTreatedAsZero', e.target.checked)}
            data-testid="abs-aro-treated-as-zero"
          />
          <span className="abs-knob-label">ARO treated as $0 (explicit)</span>
          {labels?.aroTreatedAsZero && (
            <span className="abs-knob-hint">{labels.aroTreatedAsZero}</span>
          )}
        </label>
        <label className="abs-knob abs-knob-check">
          <input
            type="checkbox"
            checked={Boolean(structure.aroUnknown)}
            onChange={(e) => onChange('aroUnknown', e.target.checked)}
            data-testid="abs-aro-unknown"
          />
          <span className="abs-knob-label">ARO unknown (block optimistic collateral)</span>
          {labels?.aroUnknown && <span className="abs-knob-hint">{labels.aroUnknown}</span>}
        </label>
      </div>
      <button type="button" className="abs-run-btn" disabled={running} onClick={onRun}>
        {running ? 'Solving…' : 'Apply structure'}
      </button>
    </section>
  );
}

function AbsCoverage({ coverage, output }) {
  if (!coverage) return null;
  const annual = coverage.annual || [];
  const months = (coverage.months || []).filter((_, i) => i % 3 === 0).slice(0, 24);
  const priceRows = absCoveragePriceRows(output);
  const deckLabel = absDeckProvenanceLabel(output);
  return (
    <section className="abs-panel">
      <h3>Coverage / covenants</h3>
      <p className="abs-panel-lead">
        Min DSCR {fmtX(coverage.minDscr)} in {coverage.bindingMonth || '—'} · every month covers debt service {fmtX(coverage.targetDscr)} ·
        breach months {coverage.breachMonths}
        {coverage.pvUnitLabel ? ` · ${coverage.pvUnitLabel}` : ''}
      </p>
      <p className="abs-panel-lead" data-testid="abs-deck-provenance">
        Price deck: {deckLabel}
      </p>
      {priceRows.rows.length > 0 && (
        <>
          <h4>Price build (sample)</h4>
          <p className="abs-panel-lead">
            First months of the priced case, by well and product. Lease names replace seller property ids.
          </p>
          <div className="abs-table-wrap">
            <table className="abs-table" data-testid="abs-priced-lines">
              <thead>
                <tr>
                  {priceRows.columns.map((c) => (
                    <th key={c}>{priceRows.headers[c]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {priceRows.rows.map((r, idx) => (
                  <tr key={`${r.date}-${r.well}-${r.product}-${idx}`}>
                    {priceRows.columns.map((c) => (
                      <td key={c}>
                        {c === 'date' || c === 'well' || c === 'product'
                          ? r[c]
                          : r[c] == null || Number.isNaN(Number(r[c]))
                            ? '—'
                            : Number(r[c]).toLocaleString('en-US', { maximumFractionDigits: 2 })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="abs-table-wrap">
        <table className="abs-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>CF</th>
              <th>Debt service</th>
              <th>DSCR</th>
            </tr>
          </thead>
          <tbody>
            {annual.map((r) => (
              <tr key={r.yearIndex}>
                <td>Y{r.yearIndex}</td>
                <td>{fmtMM(r.cf / 1e6)}</td>
                <td>{fmtMM(r.debtService / 1e6)}</td>
                <td>{fmtX(r.dscr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4>Monthly sample (every 3rd month)</h4>
      <div className="abs-table-wrap">
        <table className="abs-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>CF</th>
              <th>DS</th>
              <th>DSCR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {months.map((r) => (
              <tr key={r.date} className={r.breach ? 'abs-breach' : ''}>
                <td>{r.date}</td>
                <td>${(r.cf / 1000).toFixed(0)}k</td>
                <td>${(r.debtService / 1000).toFixed(0)}k</td>
                <td>{fmtX(r.dscr)}</td>
                <td>{r.breach ? 'breach' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AbsBid({ output }) {
  const solve = output.solve || {};
  return (
    <section className="abs-panel">
      <h3>Bid / Illustrative max purchase price (sponsor assumptions)</h3>
      <p className="abs-panel-lead">
        Illustrative max purchase price = executable note × (1 − debt fee) ÷ advPp.
        The debt fee is an up-front haircut on note proceeds, not an annual fee. Annual servicing defaults to 0.
        Axis for the bid curve: Advance vs purchase price (advPp). Price band: WTI ±$5, gas HH ±$0.50.
      </p>
      <dl className="abs-dl">
        <div>
          <dt>Illustrative max purchase price (sponsor assumptions)</dt>
          <dd className="abs-em">{fmtMM(solve.illustrativeMaxPurchasePriceMM)}</dd>
        </div>
        <div><dt>Max note size</dt><dd>{fmtMM(solve.maxNoteSizeMM)}</dd></div>
        <div><dt>DSCR capacity (not executable)</dt><dd>{fmtMM(solve.noteByDscrMM)}</dd></div>
        <div><dt>By advPv10 cap</dt><dd>{fmtMM(solve.noteByAdvPv10MM ?? solve.noteByAdvanceMM)}</dd></div>
        <div><dt>advPv10 / advPp</dt><dd>{fmtPct(solve.advPv10)} / {fmtPct(solve.advPp)}</dd></div>
        <div><dt>Bid ceiling ratio</dt><dd>{solve.bidCeilingRatio != null ? Number(solve.bidCeilingRatio).toFixed(3) : '—'}</dd></div>
        <div><dt>Binding</dt><dd>{solve.bindingConstraint}</dd></div>
        <div><dt>Collateral PV</dt><dd>{fmtMM(solve.collateralPvMM)}</dd></div>
        <div><dt>ARO treatment</dt><dd>{solve.aroTreatment || '—'}</dd></div>
        <div><dt>ARO collateral</dt><dd>{fmtMM(solve.aroCollateralMM)}</dd></div>
      </dl>
    </section>
  );
}

function AbsSensitivity({ model, oil = false, priceLabel = 'HH' }) {
  const rows = model?.rows || [];
  if (!rows.length) {
    return (
      <section className="abs-panel" data-testid="abs-sensitivity-empty">
        <h3>Sensitivity</h3>
        <p className="abs-panel-lead">
          No sensitivity cases for this run. Re-run ABS to price the base and downside cases.
        </p>
      </section>
    );
  }
  return (
    <section className="abs-panel">
      <h3>Sensitivity</h3>
      <p className="abs-panel-lead" data-testid="abs-price-band">
        ABS price band: WTI ±$5, and gas HH ±$0.50. The Value step uses WTI ±$10. Stress is sized to the product
        (oil −$5/bbl and netback −$2.50/bbl, or gas −$0.50/MMBtu and netback −$0.15/Mcf).
      </p>
      {model?.source === 'screen' ? (
        <p className="abs-panel-lead">
          Base, downside, and stress cases already priced on this screen.
        </p>
      ) : null}
      <div className="abs-table-wrap">
        <table className="abs-table" data-testid="abs-sensitivity-table">
          <thead>
            <tr>
              <th>Case</th>
              <th data-testid="abs-sensitivity-price-header">{priceLabel}</th>
              <th>Target DSCR</th>
              <th>Max bid</th>
              <th>Note</th>
              <th>Binding</th>
              <th>Min DSCR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const price = absRowPrice(r, oil);
              return (
                <tr key={r.id}>
                  <td>{r.label}</td>
                  <td>{price != null ? `$${price.toFixed(2)}` : '—'}</td>
                  <td>{fmtX(r.targetDscr)}</td>
                  <td>{fmtMM(r.illustrativeMaxPurchasePriceMM)}</td>
                  <td>{fmtMM(r.maxNoteSizeMM)}</td>
                  <td>{r.bindingConstraint}</td>
                  <td>{fmtX(r.minDscr)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AbsDeskPresets({ screen, downsideTitle }) {
  const presets = [];
  if (screen?.base?.meta?.preset) presets.push(screen.base.meta.preset);
  if (screen?.downside?.meta?.preset) presets.push(screen.downside.meta.preset);
  if (screen?.stress?.meta?.preset) presets.push(screen.stress.meta.preset);
  return (
    <section className="abs-panel" data-testid="abs-desk-presets">
      <h3>Desk presets</h3>
      <p className="abs-panel-lead">
        Classified calibration inputs — not agency decks or market quotes.
        {downsideTitle ? ` Downside column: ${downsideTitle}.` : ''}
      </p>
      {presets.length === 0 ? (
        <p>presets pending review</p>
      ) : (
        <div className="abs-table-wrap">
          <table className="abs-table">
            <thead>
              <tr>
                <th>Preset</th>
                <th>Param</th>
                <th>Value</th>
                <th>Provenance</th>
              </tr>
            </thead>
            <tbody>
              {presets.flatMap((preset) =>
                (preset.params || []).map((p) => (
                  <tr key={`${preset.presetId}-${p.paramId}`}>
                    <td>{preset.presetId === 'first-issuer-downside' ? (preset.subtitle || downsideTitle) : preset.label}</td>
                    <td>{p.key}</td>
                    <td>{String(p.value)}</td>
                    <td>{formatPresetProvenance(p)}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AbsScenarios({ rows }) {
  return (
    <section className="abs-panel">
      <h3>Desk presets</h3>
      <div className="abs-table-wrap">
        <table className="abs-table">
          <thead>
            <tr>
              <th>Preset</th>
              <th>Max bid</th>
              <th>Note</th>
              <th>Binding</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td>{fmtMM(r.illustrativeMaxPurchasePriceMM)}</td>
                <td>{fmtMM(r.maxNoteSizeMM)}</td>
                <td>{r.bindingConstraint}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
